import { useState } from 'react'
import { Minus, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { T } from '@/lib/inventoryFlow/strings'

/** Weight/volume units step by half, counted units by one. */
export function stepFor(unit: string): number {
  // 'liter' is how this app spells the stock unit (inventory-units.ts).
  return ['kg', 'g', 'l', 'ltr', 'litre', 'liter', 'liters', 'litres', 'ml'].includes(unit.toLowerCase()) ? 0.5 : 1
}

export function roundQty(n: number): number {
  return Math.round(n * 1000) / 1000
}

/** What a typed quantity becomes. Empty or unreadable → `min` (as before);
 *  `integer` rounds to a whole number and says so. */
export function parseTypedQty(text: string, opts: { min?: number; max?: number; integer?: boolean }): { value: number; rounded: boolean } {
  const { min = 0, max, integer = false } = opts
  const raw = parseFloat(text)
  let x = Number.isFinite(raw) ? raw : 0
  const rounded = integer && Number.isFinite(raw) && Math.round(raw) !== raw
  if (integer) x = Math.round(x)
  if (x < min) x = min
  if (max != null && x > max) x = max
  return { value: roundQty(x), rounded }
}

/**
 * One keystroke in the quantity box (pilot fix, Batch 5 lane tablet). The box
 * is plain text with a decimal keypad, so the browser never rewrites half-typed
 * numbers ("2." or ".") into an empty box.
 *  - `text`: what the box shows now (an unreadable keystroke keeps `prev`);
 *  - `value`: the number to pass up now, or null to wait for blur — an empty
 *    box, a lone ".", or a number still below `min` ("0" on the way to "0.5")
 *    is committed on blur instead;
 *  - above `max`, or a decimal in whole-number mode, is fixed straight away.
 */
export function typedQtyStep(
  typed: string,
  prev: string,
  opts: { min?: number; max?: number; integer?: boolean },
): { text: string; value: number | null; rounded: boolean } {
  const { min = 0, max, integer = false } = opts
  const t = typed.replace(',', '.').replace(/\s+/g, '')
  if (!/^\d*\.?\d*$/.test(t)) return { text: prev, value: null, rounded: false }
  if (t === '' || t === '.') return { text: t, value: null, rounded: false }
  const raw = parseFloat(t)
  if (integer && !Number.isInteger(raw)) {
    const r = parseTypedQty(t, opts)
    return { text: String(r.value), value: r.value, rounded: true }
  }
  if (max != null && raw > max) return { text: String(roundQty(max)), value: roundQty(max), rounded: false }
  if (raw < min) return { text: t, value: null, rounded: false }
  return { text: t, value: roundQty(raw), rounded: false }
}

/**
 * Quantity control: − [ value ] +. The value may be typed;
 * it never goes below `min` or above `max`. While the box is being typed in
 * it shows exactly what was typed ("2.", "0.5", an empty box); a finished
 * number reaches the parent as it is typed, and an empty or unfinished box is
 * committed on blur (empty → `min`) — the parent decides what an empty/zero
 * amount means.
 * `integer`: whole numbers only (dish portions); decimals are rounded with a
 * short note.
 */
export function QtyStepper({
  value,
  onChange,
  unit,
  min = 0,
  max,
  className,
  label,
  integer = false,
}: {
  value: number
  onChange: (v: number) => void
  unit: string
  min?: number
  max?: number
  className?: string
  label?: string
  integer?: boolean
}) {
  const step = integer ? 1 : stepFor(unit)
  // The typed text while the box has focus; null = show the value.
  const [text, setText] = useState<string | null>(null)
  const [rounded, setRounded] = useState(false)
  const clamp = (v: number) => parseTypedQty(String(v), { min, max, integer }).value
  return (
    <div className={cn('flex items-center gap-1.5', rounded && 'flex-wrap', className)}>
      <Button type="button" variant="outline" className="h-10 w-10 shrink-0 touch-manipulation" aria-label={`Less ${label ?? ''}`}
        onClick={() => { setText(null); setRounded(false); onChange(clamp(value - step)) }} disabled={value <= min}>
        <Minus className="h-4 w-4" />
      </Button>
      <Input
        type="text"
        inputMode={integer ? 'numeric' : 'decimal'}
        autoComplete="off"
        aria-label={label}
        className="w-24 text-center font-semibold"
        value={text ?? (Number.isFinite(value) ? String(value) : '')}
        onKeyDown={(e) => { if (integer && ['.', ','].includes(e.key)) e.preventDefault() }}
        onChange={(e) => {
          const r = typedQtyStep(e.target.value, text ?? String(value), { min, max, integer })
          setRounded(r.rounded)
          setText(r.text)
          if (r.value !== null) onChange(r.value)
        }}
        onBlur={() => {
          // Commit what is in the box: empty → `min`, below `min` → `min`.
          if (text !== null) {
            const v = parseTypedQty(text, { min, max, integer }).value
            if (v !== value) onChange(v)
          }
          setText(null)
        }}
      />
      <Button type="button" variant="outline" className="h-10 w-10 shrink-0 touch-manipulation" aria-label={`More ${label ?? ''}`}
        onClick={() => { setText(null); setRounded(false); onChange(clamp(value + step)) }} disabled={max != null && value >= max}>
        <Plus className="h-4 w-4" />
      </Button>
      <span className="ml-1 min-w-[2.5rem] text-sm text-muted-foreground">{unit}</span>
      {rounded && <span role="status" className="w-full text-xs text-muted-foreground">{T.wholeNumbersOnly}</span>}
    </div>
  )
}
